// Aviso de 5 minutos antes de una videollamada (Sala CRM↔HCE, rebanada 8b; PLAN 4.10 y 9-C4/C6): la lógica, sin pantalla ni red.
//
// Qué decide (todo con la hora del SERVIDOR, nunca con la hora de pared del equipo):
//   - una cita virtual sin conectar tiene aviso desde `inicio − 5 min` y sigue teniéndolo (aunque la hora ya pasó) hasta que el médico lo cierre o se
//     conecte: «si la cita se crea con menos de 5 min, el aviso sale de inmediato» es el mismo caso, la cita ya está dentro de la ventana al aparecer;
//   - el sonido suena UNA vez al aparecer y se repite UNA vez a los 60 s (dos veces como máximo por cita y hora de cita);
//   - cerrar el aviso lo cierra en todas las pestañas y no vuelve; reagendar cambia la hora de la cita, es otro aviso;
//   - con varias pestañas, solo una avisa: la decisión de quién (`debeActuar`) y los registros compartidos (localStorage, tolerante a que no exista)
//     evitan repetir el sonido aunque dos pestañas coincidan;
//   - si el navegador bloquea el sonido, el aviso sale igual y se dice UNA sola vez «Activa el sonido para los avisos».
//
// Los avisos de «Paciente X llegó» (presencial) NO pasan por aquí: siguen sin sonido.
import type { CitaVirtual } from './telemedicinaHce';

export const ANTICIPO_AVISO_MS = 5 * 60_000;
export const REPETICION_SONIDO_MS = 60_000;
export const MAX_SONIDOS = 2;
/** La tarjeta de una cita ya empezada se queda hasta cerrarla, pero el SONIDO solo vale hasta 15 min después de su hora: abrir el HCE tarde no debe sonar por una cita vieja. */
export const LIMITE_SONIDO_TRAS_INICIO_MS = 15 * 60_000;

/** Un aviso es de ESTA cita a ESTA hora: si se reagenda, la clave cambia y el aviso vuelve a salir. */
export const claveAviso = (c: Pick<CitaVirtual, 'citaId' | 'inicio'>): string => `${c.citaId}|${c.inicio}`;

/** Citas con aviso pendiente ahora (más próxima primero). Sin hora del servidor no se decide nada. */
export function avisosDebidos(citas: readonly CitaVirtual[], ahoraSrvMs: number | null, cerrados: ReadonlySet<string>): CitaVirtual[] {
  if (ahoraSrvMs === null) return [];
  return citas
    .filter((c) => {
      const inicio = Date.parse(c.inicio);
      return c.estado === 'programada' && Number.isFinite(inicio) && ahoraSrvMs >= inicio - ANTICIPO_AVISO_MS && !cerrados.has(claveAviso(c));
    })
    .sort((a, b) => Date.parse(a.inicio) - Date.parse(b.inicio));
}

export type RegistroSonido = { veces: number; ultimoMs: number };

/** ¿Esta cita admite sonido ahora? Hasta 15 min después de su hora (hora del servidor); antes de la ventana de 5 min ya no está entre los avisos debidos. */
export function dentroDeVentanaDeSonido(c: Pick<CitaVirtual, 'inicio'>, ahoraSrvMs: number): boolean {
  const inicio = Date.parse(c.inicio);
  return Number.isFinite(inicio) && ahoraSrvMs <= inicio + LIMITE_SONIDO_TRAS_INICIO_MS;
}

/** ¿Toca sonar para este aviso? Nunca sonó → sí; sonó una vez → sí pasados 60 s; ya sonó dos veces → no. */
export function sonidoDebido(registro: RegistroSonido | undefined, ahoraSrvMs: number): boolean {
  if (!registro || registro.veces <= 0) return true;
  if (registro.veces >= MAX_SONIDOS) return false;
  return ahoraSrvMs - registro.ultimoMs >= REPETICION_SONIDO_MS;
}

/**
 * ¿Esta pestaña es la que avisa? La que tiene el bloqueo de «pestaña visible» sí. Si ninguna pestaña visible lo tiene (todas ocultas o minimizadas) avisa
 * la que tiene el bloqueo de respaldo, para que el médico oiga el aviso aunque esté en otra ventana.
 */
export function debeActuar(o: { liderVisible: boolean; liderRespaldo: boolean; hayLiderVisible: boolean }): boolean {
  return o.liderVisible || (o.liderRespaldo && !o.hayLiderVisible);
}

// --- Registros compartidos entre pestañas (localStorage, con respaldo en memoria si no existe o falla) --------------------------------------

export type AlmacenAvisos = {
  leer: (clave: string) => string | null;
  escribir: (clave: string, valor: string) => void;
};

/** Almacén que NUNCA lanza: lee del `Storage` si puede (así lo comparten las pestañas) y, si falla, de la memoria de esta pestaña. */
export function crearAlmacenTolerante(storage: Pick<Storage, 'getItem' | 'setItem'> | null = obtenerLocalStorage()): AlmacenAvisos {
  const memoria = new Map<string, string>();
  return {
    leer(clave) {
      try {
        if (storage) return storage.getItem(clave);
      } catch {
        // modo privado o sin permiso: se cae a la memoria de esta pestaña
      }
      return memoria.get(clave) ?? null;
    },
    escribir(clave, valor) {
      memoria.set(clave, valor);
      try {
        storage?.setItem(clave, valor);
      } catch {
        // sin almacenamiento: el registro vale solo para esta pestaña
      }
    },
  };
}

function obtenerLocalStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

const CLAVE_CERRADOS = 'lc-telemed-avisos-cerrados';
const CLAVE_SONIDOS = 'lc-telemed-avisos-sonidos';
const CLAVE_MENSAJE_SONIDO = 'lc-telemed-sonido-bloqueado-avisado';
const MAX_REGISTROS = 60;

function leerJson<T>(almacen: AlmacenAvisos, clave: string, valorPorDefecto: T): T {
  try {
    const crudo = almacen.leer(clave);
    return crudo ? (JSON.parse(crudo) as T) : valorPorDefecto;
  } catch {
    return valorPorDefecto;
  }
}

export function leerCerrados(almacen: AlmacenAvisos): Set<string> {
  const lista = leerJson<unknown>(almacen, CLAVE_CERRADOS, []);
  return new Set(Array.isArray(lista) ? lista.filter((x): x is string => typeof x === 'string') : []);
}

export function cerrarAviso(almacen: AlmacenAvisos, clave: string): void {
  const todos = [...leerCerrados(almacen).add(clave)];
  almacen.escribir(CLAVE_CERRADOS, JSON.stringify(todos.slice(-MAX_REGISTROS)));
}

export function leerSonidos(almacen: AlmacenAvisos): Record<string, RegistroSonido> {
  const crudo = leerJson<unknown>(almacen, CLAVE_SONIDOS, {});
  const salida: Record<string, RegistroSonido> = {};
  if (crudo && typeof crudo === 'object') {
    for (const [k, v] of Object.entries(crudo as Record<string, unknown>)) {
      const r = v as Partial<RegistroSonido> | null;
      if (r && typeof r.veces === 'number' && typeof r.ultimoMs === 'number') salida[k] = { veces: r.veces, ultimoMs: r.ultimoMs };
    }
  }
  return salida;
}

export function registrarSonido(almacen: AlmacenAvisos, clave: string, ahoraSrvMs: number): void {
  const todos = leerSonidos(almacen);
  todos[clave] = { veces: (todos[clave]?.veces ?? 0) + 1, ultimoMs: ahoraSrvMs };
  const claves = Object.keys(todos).slice(-MAX_REGISTROS);
  almacen.escribir(CLAVE_SONIDOS, JSON.stringify(Object.fromEntries(claves.map((k) => [k, todos[k]]))));
}

/** «Activa el sonido para los avisos» se muestra una sola vez (entre pestañas y recargas, mientras el almacenamiento funcione). */
export function yaSeAvisoSonidoBloqueado(almacen: AlmacenAvisos): boolean {
  return almacen.leer(CLAVE_MENSAJE_SONIDO) === '1';
}
export function marcarSonidoBloqueadoAvisado(almacen: AlmacenAvisos): void {
  almacen.escribir(CLAVE_MENSAJE_SONIDO, '1');
}

// --- Sonido ---------------------------------------------------------------------------------------------------------------------------------

/** Lo mínimo de `AudioContext` que se usa (se sustituye en las pruebas). */
export type ContextoAudio = {
  readonly state: string;
  readonly currentTime: number;
  readonly destination: unknown;
  resume: () => Promise<void>;
  close?: () => Promise<void>;
  createOscillator: () => {
    type: string;
    frequency: { value: number };
    connect: (d: unknown) => void;
    start: (t: number) => void;
    stop: (t: number) => void;
  };
  createGain: () => {
    gain: { setValueAtTime: (v: number, t: number) => void; exponentialRampToValueAtTime: (v: number, t: number) => void };
    connect: (d: unknown) => void;
  };
};

export type Reproductor = {
  /** Se llama dentro de un gesto del usuario (clic o tecla): después el navegador deja sonar sin gesto. Devuelve si quedó activo. */
  desbloquear: () => Promise<boolean>;
  /** Suena el aviso. `false` = el navegador lo bloqueó (todavía no hubo un gesto del usuario en esta pestaña). */
  sonar: () => Promise<boolean>;
  /** Libera el AudioContext (al desmontar o cerrar sesión): los navegadores limitan cuántos pueden estar vivos. Un uso posterior crea otro. */
  cerrar: () => void;
};

const corriendo = (c: { state: string }): boolean => c.state === 'running';

/** Tres tonos cortos (agudo-grave-agudo), ~1 s, sin archivos de audio. */
function tocar(c: ContextoAudio): void {
  const inicio = c.currentTime;
  for (const [desfase, frecuencia] of [[0, 880], [0.3, 660], [0.6, 880]] as const) {
    const osc = c.createOscillator();
    const ganancia = c.createGain();
    osc.type = 'sine';
    osc.frequency.value = frecuencia;
    ganancia.gain.setValueAtTime(0.0001, inicio + desfase);
    ganancia.gain.exponentialRampToValueAtTime(0.3, inicio + desfase + 0.03);
    ganancia.gain.exponentialRampToValueAtTime(0.0001, inicio + desfase + 0.26);
    osc.connect(ganancia);
    ganancia.connect(c.destination);
    osc.start(inicio + desfase);
    osc.stop(inicio + desfase + 0.3);
  }
}

export function crearReproductor(crearContexto: () => ContextoAudio | null = crearContextoDelNavegador): Reproductor {
  let contexto: ContextoAudio | null = null;

  function obtener(): ContextoAudio | null {
    if (!contexto) {
      try {
        contexto = crearContexto();
      } catch {
        contexto = null;
      }
    }
    return contexto;
  }

  /** Un contexto creado sin gesto del usuario queda «suspended» y `resume()` no responde hasta que haya uno: no se espera sin límite. */
  async function preparar(esperaMs: number): Promise<ContextoAudio | null> {
    const c = obtener();
    if (!c) return null;
    if (!corriendo(c)) {
      let temporizador: ReturnType<typeof setTimeout> | undefined;
      try {
        await Promise.race([c.resume(), new Promise<void>((resolver) => (temporizador = setTimeout(resolver, esperaMs)))]);
      } catch {
        // resume() rechazado: sigue bloqueado
      } finally {
        if (temporizador) clearTimeout(temporizador);
      }
    }
    return corriendo(c) ? c : null;
  }

  return {
    async desbloquear() {
      return (await preparar(1000)) !== null;
    },
    cerrar() {
      const c = contexto;
      contexto = null;
      try {
        void c?.close?.().catch(() => undefined);
      } catch {
        // ya cerrado
      }
    },
    async sonar() {
      const c = await preparar(300);
      if (!c) return false;
      try {
        tocar(c);
        return true;
      } catch {
        return false;
      }
    },
  };
}

function crearContextoDelNavegador(): ContextoAudio | null {
  const Ctor = (globalThis as unknown as { AudioContext?: new () => ContextoAudio; webkitAudioContext?: new () => ContextoAudio }).AudioContext ??
    (globalThis as unknown as { webkitAudioContext?: new () => ContextoAudio }).webkitAudioContext;
  return Ctor ? new Ctor() : null;
}

/**
 * Un gesto del usuario (clic, toque o tecla) desbloquea el audio (política de reproducción automática de los navegadores). Los listeners se quitan SOLO cuando
 * `desbloquear()` confirma que quedó activo: un primer intento fallido (contexto aún suspendido) no deja la pestaña muda para siempre. Devuelve la función que
 * los deshace.
 */
export function instalarDesbloqueoDeAudio(
  reproductor: Pick<Reproductor, 'desbloquear'>,
  doc: Pick<Document, 'addEventListener' | 'removeEventListener'> = document,
): () => void {
  const eventos = ['pointerdown', 'pointerup', 'touchend', 'click', 'keydown'] as const;
  let enCurso = false;
  const quitar = (): void => eventos.forEach((e) => doc.removeEventListener(e, alGesto, true));
  function alGesto(): void {
    if (enCurso) return;
    enCurso = true;
    void reproductor
      .desbloquear()
      .then((ok) => {
        if (ok) quitar();
      })
      .catch(() => undefined)
      .finally(() => {
        enCurso = false;
      });
  }
  eventos.forEach((e) => doc.addEventListener(e, alGesto, true));
  return quitar;
}

/**
 * Hace sonar el aviso si a alguno de los `debidos` le toca (una vez al aparecer, otra a los 60 s). Marca el sonido ANTES de reproducirlo: si dos pestañas
 * coincidieran, la segunda ya lo ve registrado. Un solo sonido aunque toquen varios avisos a la vez. Devuelve `true` si sonó.
 * Si el navegador lo bloquea, el registro queda igual (no se reintenta cada segundo) y `alBloqueado` se llama UNA sola vez en toda la vida del almacenamiento.
 */
export async function sonarSiToca(o: {
  debidos: readonly CitaVirtual[];
  ahoraSrvMs: number;
  almacen: AlmacenAvisos;
  reproductor: Pick<Reproductor, 'sonar'>;
  alBloqueado: () => void;
}): Promise<boolean> {
  const sonidos = leerSonidos(o.almacen);
  const quienes = o.debidos.filter((c) => dentroDeVentanaDeSonido(c, o.ahoraSrvMs) && sonidoDebido(sonidos[claveAviso(c)], o.ahoraSrvMs));
  if (quienes.length === 0) return false;
  for (const c of quienes) registrarSonido(o.almacen, claveAviso(c), o.ahoraSrvMs);
  const sono = await o.reproductor.sonar();
  if (!sono && !yaSeAvisoSonidoBloqueado(o.almacen)) {
    marcarSonidoBloqueadoAvisado(o.almacen);
    o.alBloqueado();
  }
  return sono;
}

/** ¿Alguno de los avisos debidos todavía tiene sonido pendiente? (comprobación barata, para no hacer nada más cada segundo). */
export function hayQueSonar(debidos: readonly CitaVirtual[], ahoraSrvMs: number, almacen: AlmacenAvisos): boolean {
  const sonidos = leerSonidos(almacen);
  return debidos.some((c) => dentroDeVentanaDeSonido(c, ahoraSrvMs) && sonidoDebido(sonidos[claveAviso(c)], ahoraSrvMs));
}
